import ReactGridLayout from 'react-grid-layout';
import domtoimage from 'dom-to-image-more';

export const inventoryOptions = [
  { value: 'new_alerts', label: 'New Alerts' },
  { value: 'resolved_alerts', label: 'Resolved Alerts' },
  { value: 'edges', label: 'Edges' },
  { value: 'tunnels', label: 'Tunnels' },
];

export interface IDashboard {
  id?: string;
  name: string;
  widgets?: Array<Widget>;
  image?: string;
  createdBy?: string;
  dateCreated?: string;
  lastModifiedBy?: string;
  dateModified?: string;
  userId?: string | number;
  theme?: string;
}
export interface IDashboardShare {
  directShareTenantId?: Array<number>;
  directShareGroupId?: Array<number>;
  inheritGroupsOfTenantId?: Array<number>;
  inheritTenantsOfTenantId?: Array<number>;
  createdBy?: string;
  dateCreated?: string;
  lastModifiedBy?: string;
  dateModified?: string;
}

export class IServerEntity {
  id?: string;
  name: string;
  widgets?: Array<any>;
  image?: string;
  createdBy?: string;
  dateCreated?: string;
  lastModifiedBy?: string;
  dateModified?: string;
}

export type Widget = {
  id?: number;
  definition: WidgetDefinition;
  layout: WidgetLayout;
};

export type VisualizationTypes = 'line' | 'histogram' | 'sdWan' | 'table' | 'gauge' | 'demoLine' | 'pie' | 'bar' | 'table' | 'map';
export type Operation = 'SUM' | 'AVG' | 'MAX' | 'MIN' | 'COUNT' | 'ANOMALY' | 'PREDICTION';

export type WidgetDefinition = {
  title: string;
  type: VisualizationTypes;
  colorScheme: { label: string; value: string };
  dataQuery?: DataQuery;
  widgetOption: string;
};

export type DataQuery = {
  queries?: Array<Query>;
  metric?: Array<string>;
  singleMetric?: string;
  operation?: Operation | '';
  controller?: string;
  inventoryType?: string;
  tenantName?: string;
  namespace?: string;
  org?: string;
  city?: { label: string; value: string };
  deviceStatus?: string
};

export type Query = string;

export type WidgetLayout = {
  height: number;
  width: number;
  x: number;
  y: number;
};

export const toWidget = (data: WidgetItem): Widget => {
  return {
    id: parseInt(data.i, 10),
    layout: {
      height: data.h,
      width: data.w,
      x: data.x,
      y: data.y,
    },
    definition: data.definition,
  };
};

export const toGridItem = (item: Widget): WidgetItem => {
  return {
    h: item.layout.height,
    i: item.id?.toString(),
    w: item.layout.width,
    x: item.layout.x,
    y: item.layout.y,
    definition: item.definition,
  };
};

export const dashboardsData: IDashboard[] = [];

export const defaultValue: Readonly<IDashboard> = null;

export interface WidgetItem extends ReactGridLayout.Layout {
  h: number;
  i: string;
  isDraggable?: boolean;
  isResizable?: boolean;
  static?: boolean;
  w: number;
  x: number;
  y: number;
  definition?: WidgetDefinition;
}

export type TimeLine = {
  startTime: string;
  endTime: string;
  value: number;
  measurement: string;
  field: string;
  time: string;
  vmName: string;
  job: string;
  dcName?: string;
  cluster: string;
};

export type GridLayoutState = {
  cols: number;
  showAddWidget: boolean;
  editWidgetItem?: Widget;
  breakpoint: unknown;
};

export const DisplayType = [
  {
    name: 'Line Chart',
    key: 'line',
  },
  {
    name: 'Histogram',
    key: 'histogram',
  },
  {
    name: 'Single Stat',
    key: 'single stat',
  },
  {
    name: 'Gauge',
    key: 'gauge',
  },
  {
    name: 'Map',
    key: 'map',
  },
];

export type FormProps = {
  widgetName: string;
  visualizationDisplay?: VisualizationTypes | null;
  metric?: Array<{ name: string; value: string }> | null;
  singleMetric?: { name: string; value: string } | null;
  operation?: Operation;
  queries?: Array<string>;
  widgetOption: string;
  controller?: any;
  tenant?: string;
  inventoryType?: { title: string; value: string } | null;
  colorScheme: string | null;
};
export type DASHBOARD_MODE = 'edit' | 'view';

export const composeWidgetPayload = (values, organization) => {
  const payload = {
    type: values.visualizationDisplay?.value,
    widgetOption: values.widgetOption,
    title: values.widgetName,
    colorScheme: values.colorScheme,
    dataQuery: {
      metric: values.widgetOption === 'br' ? (values.metric?.length > 0 ? values.metric.map(m => m.value) : []) : [],
      queries: values.widgetOption === 'br' ? (values?.nodes?.length > 0 ? values?.nodes?.map(m => m?.value) : []) : [],
      operation: values.operation,
      org: organization?.id,
      tenantName: organization?.name,
      city: values.city,
      deviceStatus: values.deviceStatus
    },
  };

  return payload;
};

export const mapWidgetToFormValues = (config, organizations) => {
  const formValues = {
    colorScheme: config.colorScheme,
    widgetName: config.title,
    visualizationDisplay: {
      value: DisplayType?.find(type => type.key === config.type)?.key,
      label: DisplayType?.find(type => type.key === config.type)?.name,
    },
    metric: config.dataQuery?.metric.map(m => ({ label: m, value: m })),
    operation: config.dataQuery?.operation,
    nodes: config.dataQuery?.queries ? config.dataQuery?.queries.map(m => ({ label: m, value: m })) : [],
    widgetOption: config.widgetOption ? config.widgetOption : 'br',
    tenant: config.dataQuery?.org,
    city: config.dataQuery?.city,
    deviceStatus: config.dataQuery?.deviceStatus,
  };
  return formValues;
};

export const getNewWidgetPosition = (widgets, cols, minWidth) => {
  let xPoint = 0;
  let yPoint = 0;
  if (widgets && widgets.length > 0) {
    const columns = cols || 12;
    /* find last row */
    const lastRow = Math.max(...widgets.map(w => w.layout.y));
    const maxXInRow = Math.max(...widgets.filter(w => w.layout.y === lastRow).map(w => w.layout.x));
    const maxHeightInRow = Math.max(...widgets.filter(w => w.layout.y === lastRow).map(w => w.layout.height));

    /* find last widget */
    const lastWidget = widgets.find(w => w.layout.x === maxXInRow && w.layout.y === lastRow);

    /* If the row has space add item there otherwise add to next row */
    xPoint = columns - (lastWidget.layout.x + lastWidget.layout.width) >= minWidth ? lastWidget.layout.x + lastWidget.layout.width : 0;
    yPoint = columns - (lastWidget.layout.x + lastWidget.layout.width) >= minWidth ? lastRow : lastRow + maxHeightInRow;
  }
  return { xPoint, yPoint };
};

export const composeSaveDashboardPayload = (dashboardDetails, widgetItems) => {
  const payload = {
    name: dashboardDetails.title,
    description: dashboardDetails.description,
    theme: dashboardDetails.theme,
    /**
     * There is not description field on front-end in MVP
     */
    // description: dashboardDetails.description,
    image: dashboardDetails.image,
    widgets: widgetItems,
  };
  if (dashboardDetails.id) {
    payload['id'] = dashboardDetails.id;
  }
  return payload;
};

export const metrics = [
  { value: 'dio_RLY_DRIVE', label: 'dio_RLY_DRIVE' },
  { value: 'rs_thresh', label: 'rs_thresh' },
  { value: 'adc_mcu_temp_C', label: 'adc_mcu_temp_C' },
  { value: 'fw_rst_cause', label: 'fw_rst_cause' },
  { value: 'fw_git_tag', label: 'fw_git_tag' },
  { value: 'fw_git_hash',label: 'fw_git_hash'},
  { value: 'dio_5V_PG', label: 'dio_5V_PG' },
  { value: 'dio_OCb', label: 'dio_OCb' },
  { value: 'rs_serial', label: 'rs_serial' },
  { value: 'aws_thing_name', label: 'aws_thing_name' },
  { value: 'rs_lat', label: 'rs_lat' },
  { value: 'rs_long', label: 'rs_long' },
  { value: 'cell_rsrp_dBm', label: 'cell_rsrp_dBm' },
  { value: 'cell_ipv4', label: 'cell_ipv4' },
  { value: 'cell_time', label: 'cell_time' },
  { value: "cell_imei", label: "cell_imei"},
  { value: 'event', label: 'event' },
  { value: 'event_id', label: 'event_id' },
  { value: 'eui_mcu', label: 'eui_mcu' },
  { value: 'rs_datum_x1000', label: 'rs_datum_x1000' },
  { value: 'rs_subscribed', label: 'rs_subscribed' },
];

export const getDashboardImage = ({ widgetsRef, setImage }) => {
  const original = widgetsRef.current;
  if (!original) return;

  // Choose a fixed target size
  const targetWidth = 320;
  const targetHeight = 380;

  // Clone node
  const clonedNode = original.cloneNode(true) as HTMLElement;
  clonedNode.classList?.replace("edit-mode", "view-mode");

  // Replace canvases with static images
  const originalCanvases = original.querySelectorAll("canvas");
  const clonedCanvases = clonedNode.querySelectorAll("canvas");

  originalCanvases.forEach((canvas, i) => {
    const img = document.createElement("img");
    img.src = (canvas as HTMLCanvasElement).toDataURL();
    img.style.width = canvas.style.width;
    img.style.height = canvas.style.height;
    clonedCanvases[i].replaceWith(img);
  });

  // Wrapper
  const wrapper = document.createElement("div");
  wrapper.style.position = "absolute";
  wrapper.style.top = "-9999px";
  wrapper.style.left = "-9999px";
  wrapper.style.width = `${targetWidth}px`;
  wrapper.style.height = `${targetHeight}px`;
  wrapper.style.overflow = "hidden";

  // Scale cloned node to fit inside target size
  const scaleX = targetWidth / original.offsetWidth;
  const scaleY = targetHeight / original.offsetHeight;
  const scale = Math.min(scaleX, scaleY); // keep aspect ratio

  clonedNode.style.transform = `scale(${scale})`;
  clonedNode.style.transformOrigin = "top left";

  wrapper.appendChild(clonedNode);
  document.body.appendChild(wrapper);

  // Capture fixed-size image
  domtoimage
    .toPng(wrapper, {
      width: targetWidth,
      height: targetHeight,
      bgcolor: "transparent",
      style: { transform: "none" },
    })
    .then((dataUrl) => {
      const img = new Image();
      img.src = dataUrl;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      img.onload = () => {
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        ctx?.drawImage(img, 0, 0, targetWidth, targetHeight);
        setImage(canvas);
        document.body.removeChild(wrapper);
      };
    })
    .catch((error) => {
      console.error("Error capturing image:", error);
      document.body.removeChild(wrapper);
    });
};



export const YAxisOptions = [
  { value: 'SUM', label: 'Sum()' },
  { value: 'COUNT', label: 'Count()' },
  { value: 'AVG', label: 'Avg()' },
  { value: 'MIN', label: 'Min()' },
  { value: 'MAX', label: 'Max()' },
  { value: 'NONE', label: 'None' },
];

export enum DeviceType {
  br = 'Border Router',
  nodes = 'Nodes',
  mesh = 'Mesh',
}

export const ColorSchemeOptions = [
  { name: 'Nineteen Eighty Four', key: 'NINETEEN_EIGHTY_FOUR' },
  { name: 'Atlantis', key: 'ATLANTIS' },
  { name: 'Do Androids Dream', key: 'DO_ANDROIDS_DREAM' },
  { name: 'Delorean', key: 'DELOREAN' },
  { name: 'Ectoplasm', key: 'ECTOPLASM' },
  { name: 'Primary', key: 'PRIMARY' },
  { name: 'Primary (Reverse)', key: 'PRIMARY_REVERSE' },
  { name: 'T Max 400 Film', key: 'T_MAX_400_FILM' },
  { name: 'Rainbow (8)', key: 'RAINBOW_EIGHT' },
  { name: 'Rainbow (16)', key: 'RAINBOW_SIXTEEN' },
  { name: 'Viridis', key: 'VIRIDIS' },
  { name: 'Magma', key: 'MAGMA' },
  { name: 'Inferno', key: 'INFERNO' },
  { name: 'Plasma', key: 'PLASMA' },
  { name: 'ylOrRd', key: 'YL_OR_RD' },
  { name: 'ylGnBu', key: 'YL_GN_BU' },
  { name: 'buGn', key: 'BU_GN' },
  { name: 'Solid Blue', key: 'SOLID_BLUE' },
  { name: 'Solid Green', key: 'SOLID_GREEN' },
  { name: 'Solid Red', key: 'SOLID_RED' },
  { name: 'Solid Yellow', key: 'SOLID_YELLOW' },
  { name: 'Solid Purple', key: 'SOLID_PURPLE' },
];
